import ScrollableTableWrapper from 'components/table/components/ScrollableTableWrapper';
import TableSkeleton from 'components/table/components/TableSkeleton';
import Thead from 'components/table/components/thead';
import useText from 'hooks/useText';
import { Fragment, useLayoutEffect, useRef, useState } from 'react';
import TaskGroupRow from './TaskGroupRow';
import TaskTableRow from './TaskTableRow';

const GROUP_LABELS = {
  late: 'groupLate',
  today: 'groupToday',
  tomorrow: 'groupTomorrow',
  later: 'groupLater',
  done: 'groupDone',
};

/**
 * The tasks in the admin's table (ScrollableTableWrapper, Thead, TableRow),
 * each group under its own head. `rowState(row)` gives what the page knows of
 * a row: { done, ticked, leaving, onLeaveEnd }. `groups` null: the list is
 * read, its head and grey rows in its place.
 */
const TaskTable = ({ groups, rowState, team, meId, onToggle, onOpen, onOpenCompany, onAssign }) => {
  const { text } = useText('tasks');
  // The heads of the groups stick right under the table's head: its height.
  const head = useRef(null);
  const [headHeight, setHeadHeight] = useState(49);
  useLayoutEffect(() => {
    const height = head.current?.getBoundingClientRect().height ?? 0;
    if (height) setHeadHeight(height);
  }, []);
  // « Tâche » spans the checkbox's column too: it starts at the left edge,
  // as the groups' heads below it.
  const header = [
    { label: text('columnTask'), colSpan: 2 },
    text('columnType'),
    text('columnLinked'),
    text('columnDue'),
    text('columnTime'),
    // The assignee's avatar speaks for itself: no title over it.
    '',
  ];
  // Thead adds an empty column for the actions: every row has one more cell.
  const columns = header.reduce((sum, item) => sum + (item.colSpan ?? 1), 0) + 1;
  const indexed = (groups ?? []).reduce(
    (out, group) => [...out, { ...group, from: out.reduce((sum, item) => sum + item.rows.length, 0) }],
    [],
  );
  return (
    <ScrollableTableWrapper>
      {/* Separate borders, drawn by the cells (a row's own border is not, in
          this model): a stuck cell carries them along, where it would leave
          collapsed ones behind. The head's line runs under every column. */}
      <table className="w-full table-auto border-separate border-spacing-0 text-gray-500 ft-sm [&>tbody>tr>td]:border-b [&>tbody>tr>td]:border-gray-100 [&>thead>tr>th]:border-gray-100">
        <thead ref={head} className="sticky top-0 z-10">
          <Thead champsHeader={header} pathname="/tasks" />
        </thead>
        {!groups && <TableSkeleton columns={columns} rows={6} label="Chargement des tâches" />}
        {groups && (
          <tbody>
            {indexed.map((group) => (
              <Fragment key={group.id}>
                <TaskGroupRow
                  label={text(GROUP_LABELS[group.id])}
                  count={group.rows.length}
                  columns={columns}
                  top={headHeight}
                />
                {group.rows.map((row, position) => (
                  <TaskTableRow
                    key={row.key}
                    row={row}
                    index={group.from + position}
                    {...rowState(row)}
                    team={team}
                    meId={meId}
                    onToggle={onToggle}
                    onOpen={onOpen}
                    onOpenCompany={onOpenCompany}
                    onAssign={onAssign}
                  />
                ))}
              </Fragment>
            ))}
          </tbody>
        )}
      </table>
    </ScrollableTableWrapper>
  );
};

export default TaskTable;
